/**
 * usePasskey Hook
 *
 * React hook for Passkey (WebAuthn/FIDO2) authentication.
 * Provides passwordless login using Face ID, Touch ID, Windows Hello, etc.
 *
 * @example
 * const { registerPasskey, loginWithPasskey, listPasskeys } = usePasskey();
 */

import { useState } from 'react';
import { BackendAuth } from '@/lib/auth-backend';

// Persist a passkey-login response the same way BackendAuth.login() does, so the web
// frontend recognises the session. BackendAuth reads getToken()→'auth_token' and
// getUser()→'user_data'; storing only 'access_token'/'user' (the mobile-app keys) left
// the web app logged out → it bounced straight back to /signin after a successful login.
function persistPasskeyAuth(response) {
  try {
    const token = response.access_token;
    const userJson = JSON.stringify(response.user || {});
    localStorage.setItem('auth_token', token);     // web main key (BackendAuth.getToken)
    localStorage.setItem('access_token', token);   // mobile-app compatibility
    if (response.refresh_token) localStorage.setItem('refresh_token', response.refresh_token);
    localStorage.setItem('user_data', userJson);   // web (BackendAuth.getUser)
    localStorage.setItem('user', userJson);        // mobile compatibility
    // auth-token cookie for <img>/<iframe>/subdomain requests (mirror password login)
    const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `auth-token=${token}; expires=${expires}; path=/; SameSite=Lax`;
  } catch (_) { /* storage unavailable */ }
}

export function usePasskey() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Register a new passkey for current user
   *
   * @param {string} deviceName - User-friendly device name
   * @returns {Promise<object>} Registration result
   */
  const registerPasskey = async (deviceName) => {
    setIsLoading(true);
    setError(null);

    try {
      // Check WebAuthn API availability
      if (!navigator.credentials || !navigator.credentials.create) {
        const errorMessage = 'Passkeys are not supported in this browser. Ensure you are using HTTPS or localhost.';
        setError(errorMessage);
        setIsLoading(false);
        throw new Error(errorMessage);
      }

      // Check secure context (HTTPS requirement)
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        const errorMessage = 'Passkeys require a secure context (HTTPS). Please access this site via HTTPS.';
        setError(errorMessage);
        setIsLoading(false);
        throw new Error(errorMessage);
      }

      // Step 1: Get registration options from backend
      const options = await BackendAuth.apiCallJson('/api/auth/passkey/register/begin', {
        method: 'POST'
      });

      // console.log('[Passkey] Registration options received:', options);

      // Step 2: Create credential using WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: {
          ...options,
          // Convert base64 strings to ArrayBuffers
          challenge: base64ToArrayBuffer(options.challenge),
          user: {
            ...options.user,
            id: base64ToArrayBuffer(options.user.id),
          },
          excludeCredentials: options.excludeCredentials?.map(cred => ({
            ...cred,
            id: base64ToArrayBuffer(cred.id),
          })) || [],
        },
      });

      if (!credential) {
        throw new Error('Passkey registration was cancelled');
      }

      // console.log('[Passkey] Credential created successfully');

      // Step 3: Send credential to backend for verification
      const response = await BackendAuth.apiCallJson('/api/auth/passkey/register/complete', {
        method: 'POST',
        body: JSON.stringify({
          credential: credentialToJSON(credential),
          device_name: deviceName || 'My Device',
        }),
      });

      // console.log('[Passkey] Registration complete:', response);

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[Passkey] Registration failed:', err);
      const errorMessage = err.name === 'NotAllowedError'
        ? 'Passkey registration was cancelled'
        : err.message || 'Failed to register passkey';
      setError(errorMessage);
      setIsLoading(false);
      // Preserve the WebAuthn error name so callers can branch (e.g. InvalidStateError
      // = this device already holds a credential for this account → not a real error).
      const wrapped = new Error(errorMessage);
      wrapped.name = err.name;
      throw wrapped;
    }
  };

  /**
   * Login with passkey
   *
   * @param {string} email - User email
   * @returns {Promise<object>} Login result with tokens
   */
  const loginWithPasskey = async (email) => {
    setIsLoading(true);
    setError(null);

    try {
      // Check WebAuthn API availability
      if (!navigator.credentials || !navigator.credentials.get) {
        const errorMessage = 'Passkeys are not supported in this browser. Ensure you are using HTTPS or localhost.';
        setError(errorMessage);
        setIsLoading(false);
        throw new Error(errorMessage);
      }

      // Check secure context (HTTPS requirement)
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        const errorMessage = 'Passkeys require a secure context (HTTPS). Please access this site via HTTPS.';
        setError(errorMessage);
        setIsLoading(false);
        throw new Error(errorMessage);
      }

      // Step 1: Get authentication options from backend
      const options = await BackendAuth.apiCallJson('/api/auth/passkey/login/begin', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      // console.log('[Passkey] Authentication options received:', options);

      // Step 2: Get credential using WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: {
          ...options,
          // Convert base64 strings to ArrayBuffers
          challenge: base64ToArrayBuffer(options.challenge),
          allowCredentials: options.allowCredentials?.map(cred => ({
            ...cred,
            id: base64ToArrayBuffer(cred.id),
          })) || [],
        },
      });

      if (!credential) {
        throw new Error('Passkey login was cancelled');
      }

      // console.log('[Passkey] Credential received successfully');

      // Step 3: Send credential to backend for verification (include email for challenge lookup)
      const response = await BackendAuth.apiCallJson('/api/auth/passkey/login/complete', {
        method: 'POST',
        body: JSON.stringify({
          credential: credentialToJSON(credential),
          email: email,  // Required for challenge lookup
        }),
      });

      // console.log('[Passkey] Login successful:', response.user);

      // Store under the keys the web frontend reads (auth_token/user_data) — see helper.
      persistPasskeyAuth(response);

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[Passkey] Login failed:', err);
      const errorMessage = err.name === 'NotAllowedError'
        ? 'Passkey login was cancelled'
        : err.message || 'Failed to login with passkey';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  /**
   * Check if the browser supports WebAuthn Conditional Mediation (Conditional UI / autofill).
   * When available, discoverable credentials that are physically present on THIS device
   * are surfaced into the email-field autofill — no server-side "does this account have a
   * passkey somewhere?" guess (which caused dead taps on the wrong device).
   *
   * @returns {Promise<boolean>} True if conditional mediation is available
   */
  const isConditionalMediationAvailable = async () => {
    try {
      return !!(
        typeof window !== 'undefined' &&
        window.PublicKeyCredential &&
        PublicKeyCredential.isConditionalMediationAvailable &&
        (await PublicKeyCredential.isConditionalMediationAvailable())
      );
    } catch {
      return false;
    }
  };

  /**
   * Passive Conditional UI login.
   *
   * Starts a `navigator.credentials.get({ mediation: 'conditional' })` that runs silently in
   * the background. The browser only offers a passkey if a discoverable credential exists
   * LOCALLY on this device, and surfaces it through the email-input autofill. No modal, no
   * forced OS dialog. The promise resolves only once the user actively picks a passkey.
   *
   * NOTE: Uses fetch() directly with credentials:'omit' for the pre-auth, CSRF-exempt
   * begin-conditional endpoint (same rationale as checkAvailability — avoid redirect loops
   * from stale tokens on the signin page).
   *
   * @param {object}      opts
   * @param {AbortSignal} opts.signal - Abort signal to cancel the conditional get (e.g. when
   *                                    the user starts a password login instead).
   * @returns {Promise<object>} Login result with tokens, or { success:false, aborted:true }
   *                            when the flow was cancelled.
   */
  const loginWithConditionalUI = async ({ signal } = {}) => {
    try {
      // WebAuthn API availability + secure context (silent — this runs passively)
      if (!navigator.credentials || !navigator.credentials.get) {
        return { success: false, unsupported: true };
      }
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        return { success: false, unsupported: true };
      }

      // Step 1: Get conditional authentication options (empty allowCredentials).
      // Pre-auth, CSRF-exempt endpoint — fetch directly with credentials:'omit'.
      const optsResponse = await fetch('/api/auth/passkey/login/begin-conditional', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({}),
        signal,
      });

      if (!optsResponse.ok) {
        return { success: false };
      }

      const options = await optsResponse.json();

      // Step 2: Conditional get — surfaces only device-local discoverable credentials.
      const credential = await navigator.credentials.get({
        mediation: 'conditional',
        signal,
        publicKey: {
          ...options,
          challenge: base64ToArrayBuffer(options.challenge),
          allowCredentials: [],
        },
      });

      if (!credential) {
        return { success: false };
      }

      // Step 3: Complete — server reads the challenge from clientDataJSON; no email needed.
      const completeResponse = await fetch('/api/auth/passkey/login/complete', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          credential: credentialToJSON(credential),
        }),
        signal,
      });

      if (!completeResponse.ok) {
        return { success: false };
      }

      const response = await completeResponse.json();

      // Store under the keys the web frontend reads (auth_token/user_data) — see helper.
      persistPasskeyAuth(response);

      return { success: true, ...response };
    } catch (err) {
      // AbortError: the conditional flow was cancelled (e.g. user chose password login,
      // or component unmounted). NotAllowedError: user dismissed the OS picker. Both are
      // expected, swallow them silently — no console noise, no error state.
      if (err.name === 'AbortError' || err.name === 'NotAllowedError') {
        return { success: false, aborted: true };
      }
      console.error('[Passkey] Conditional UI login failed:', err);
      return { success: false, error: err.message };
    }
  };

  /**
   * List user's passkeys
   *
   * @returns {Promise<Array>} List of passkeys
   */
  const listPasskeys = async () => {
    try {
      const response = await BackendAuth.apiCallJson('/api/auth/passkey/list');
      return response.passkeys;
    } catch (err) {
      console.error('[Passkey] Failed to list passkeys:', err);
      throw new Error('Failed to list passkeys');
    }
  };

  /**
   * Delete a passkey
   *
   * @param {number} credentialId - Passkey ID to delete
   * @returns {Promise<object>} Deletion result
   */
  const deletePasskey = async (credentialId) => {
    try {
      await BackendAuth.apiCallJson(`/api/auth/passkey/${credentialId}`, {
        method: 'DELETE',
      });
      return { success: true };
    } catch (err) {
      console.error('[Passkey] Failed to delete passkey:', err);
      throw new Error('Failed to delete passkey');
    }
  };

  /**
   * Check if browser supports passkeys
   *
   * @returns {boolean} True if passkeys supported
   */
  const isPasskeySupported = () => {
    return typeof window !== 'undefined' &&
           window.PublicKeyCredential !== undefined;
  };

  /**
   * Check if platform authenticator is available (Face ID, Touch ID, Windows Hello)
   *
   * @returns {Promise<boolean>} True if platform authenticator available
   */
  const isPlatformAuthenticatorAvailable = async () => {
    if (!isPasskeySupported()) return false;

    try {
      return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  };

  /**
   * Check if passkey is available for a given email (Amazon/Google-style)
   * NOTE: Uses fetch() directly with credentials: 'omit' because this is a PUBLIC endpoint
   * that should work without authentication. Using BackendAuth would cause redirect loops
   * on the signin page if old/invalid tokens exist in cookies.
   *
   * @param {string} email - User email to check
   * @returns {Promise<object>} Availability result { available, device_count, devices }
   */
  const checkAvailability = async (email) => {
    if (!email || !email.includes('@')) {
      return { available: false, device_count: 0, devices: [] };
    }

    try {
      // Use fetch() directly for public endpoints to avoid redirect loops
      // credentials: 'omit' prevents sending cookies with old/invalid tokens
      const response = await fetch('/api/auth/passkey/check-availability', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'omit', // CRITICAL: Don't send cookies to avoid token validation issues
        body: JSON.stringify({ email }),
      });

      // Handle non-OK responses gracefully
      if (!response.ok) {
        console.warn(`[Passkey] Availability check returned ${response.status}`);
        return { available: false, device_count: 0, devices: [] };
      }

      const data = await response.json();
      // console.log('[Passkey] Availability check result:', data.data);

      return data.data || { available: false, device_count: 0, devices: [] };
    } catch (err) {
      console.error('[Passkey] Availability check failed:', err);
      // Return safe default on error
      return { available: false, device_count: 0, devices: [] };
    }
  };

  return {
    registerPasskey,
    loginWithPasskey,
    loginWithConditionalUI,
    listPasskeys,
    deletePasskey,
    checkAvailability,
    isPasskeySupported,
    isPlatformAuthenticatorAvailable,
    isConditionalMediationAvailable,
    isLoading,
    error,
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert base64 string to ArrayBuffer
 * @param {string} base64 - Base64 string
 * @returns {ArrayBuffer} ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  // Remove base64url encoding (- and _ to + and /)
  const base64Standard = base64.replace(/-/g, '+').replace(/_/g, '/');

  const binaryString = atob(base64Standard);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64 string
 * @param {ArrayBuffer} buffer - ArrayBuffer
 * @returns {string} Base64 string
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  // Use base64url encoding (replace + and / with - and _)
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, ''); // Remove padding
}

/**
 * Convert PublicKeyCredential to JSON (for sending to backend)
 * @param {PublicKeyCredential} credential - WebAuthn credential
 * @returns {object} JSON representation
 */
function credentialToJSON(credential) {
  const response = credential.response;

  return {
    id: credential.id,
    rawId: arrayBufferToBase64(credential.rawId),
    response: {
      clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
      // Registration response
      attestationObject: response.attestationObject
        ? arrayBufferToBase64(response.attestationObject)
        : undefined,
      // Authentication response
      authenticatorData: response.authenticatorData
        ? arrayBufferToBase64(response.authenticatorData)
        : undefined,
      signature: response.signature
        ? arrayBufferToBase64(response.signature)
        : undefined,
      userHandle: response.userHandle
        ? arrayBufferToBase64(response.userHandle)
        : undefined,
    },
    type: credential.type,
  };
}
